// @ts-strict-ignore
import { isEmpty, isNil } from 'lodash';
import React, { useContext } from 'react';
import { RCSubMenu } from '@shared/components/RCMenu';
import { View } from '@shared/components/View';
import { Checkbox } from '@shared/components/checkbox';
import { MBLoadingIndicator } from '@shared/components/loading';
import { MBTextBody1, MBTextOverlineBold } from '@shared/components/typography';
import { RelatedCardComponent, prepareComponent } from '@shared/scenes/workflows/config';
import {
  useWorkflowDispatch,
  useWorkflowTemplateState,
} from '@shared/scenes/workflows/context/context';
import { isComponentSupportedForLookup } from '@shared/scenes/workflows/edit/components/optionsMenu/relatedCardLookup/RelatedCardLookupOptions';
import { BuilderComponentOpenContext } from '@shared/scenes/workflows/edit/context';
import { WorkspaceDetail, useWorkflowTemplate } from '@shared/scenes/workflows/hooks';
import { relatedCardLookupOption } from '@shared/scenes/workflows/template/ComponentOptionDefaults';
import {
  hasRelatedCardEntityFilter,
  hasRelatedCardFilter,
  isRelatedCardComponent,
} from '@shared/scenes/workflows/utils';
import { ColorThemeContext } from '@shared/styles';
import { i18n } from '@shared/util/i18n';
import { useSetComponentField } from '../../../hooks';
import { NestedOptionsRow } from '../NestedOptionsRow';
import { ComponentDefaultItemRow } from '../componentDefaults/row/ComponentDefaultItemRow';
import { DefaultSelection } from '../componentDefaults/selection/DefaultSelection';
import { MBWorkflowDefaultComponentOptions } from '../defaultOptions';
import { LookupComponent, RelatedCardLookupMenu } from '../relatedCardLookup/RelatedCardLookupMenu';
import { RelatedCardFilterRow } from './RelatedCardFilterRow';
import { RelatedCardFilterView } from './RelatedCardFilterView';
import { RelatedCardLinkingRow } from './RelatedCardLinkingRow';
import { RelatedCardLinkingView } from './RelatedCardLinkingView';
import { RelatedCardSortRow } from './RelatedCardSortRow';
import { RelatedCardSortView } from './RelatedCardSortView';
import styles from './styles.module.css';
import { useLinkingOptions } from './useLinkingOptions';

export interface LinkableWorkflow {
  workflowTemplateId: string;
  imageUrl: string;
  name: string;
  components: MBAPI.WorkflowTemplate_components[];
}

interface Props {
  component: RelatedCardComponent;
  workflowTemplateId: string;
  workspaceDetails: Map<string, WorkspaceDetail>;
}

export function RelatedCardLinkingOptions({
  component,
  workflowTemplateId,
  workspaceDetails,
}: Props) {
  const dispatch = useWorkflowDispatch();
  const extraSectionsGenerator = useRelatedCardOptionsSection(
    workflowTemplateId,
    component,
    workspaceDetails,
  );
  const relatedWorkflowTemplate = useWorkflowTemplate(component.workflowTemplateId);
  const lookupComponents = relatedWorkflowTemplate?.workflowTemplate?.components?.filter(
    isComponentSupportedForLookup,
  ) as LookupComponent[];
  const isLookupEnabled = !isEmpty(lookupComponents);
  const state = useWorkflowTemplateState();
  const currentComponents = state.workflowTemplate.components;
  const { componentEditorOpen, setComponentEditorOpen } = useContext(BuilderComponentOpenContext);

  const addLookupComponent = lookupComponent => {
    const label = `${lookupComponent.label} (from ${component.label})`;
    const newComponent = prepareComponent({
      ...relatedCardLookupOption.component,
      label,
      placeholder: label,
      icon: lookupComponent.icon,
      lookupComponentId: lookupComponent.id,
      relatedCardComponentId: component.id,
      lookupWorkflowTemplateId: component.workflowTemplateId,
    });
    // give menu time to close
    dispatch({
      type: 'addComponent',
      args: newComponent,
    });
    dispatch({
      type: 'reorderComponent',
      args: {
        source: currentComponents.length,
        target: currentComponents.findIndex(c => c.id === component.id) + 1,
      },
    });
    setComponentEditorOpen(newComponent.id);
  };

  // needed to remove SubMenu from dom
  if (componentEditorOpen !== component.id) {
    return null;
  }

  return (
    <MBWorkflowDefaultComponentOptions
      component={component}
      extraSections={extraSectionsGenerator}
      extraOptions={
        isLookupEnabled && (
          <RCSubMenu
            key="relatedCardLinkLookup"
            style={{ zIndex: 999 }}
            title={
              <NestedOptionsRow
                name={i18n('builder.relatedCardOptions.singleFieldLookup')}
                defaultIcon="search-look-up"
                selectedIcon="search-look-up"
              />
            }
          >
            <RelatedCardLookupMenu
              components={lookupComponents}
              onLookupSelect={addLookupComponent}
            />
          </RCSubMenu>
        )
      }
    />
  );
}

function useRelatedCardOptionsSection(
  workflowTemplateId: string,
  component: RelatedCardComponent,
  workspaceDetails: Map<string, WorkspaceDetail>,
) {
  const savedWorkflowTemplate = useWorkflowTemplate(workflowTemplateId);
  const colors = useContext(ColorThemeContext);

  const savedComponent: RelatedCardComponent =
    savedWorkflowTemplate?.workflowTemplate?.components.find(value => {
      return value.id === component.id && isRelatedCardComponent(value);
    }) as RelatedCardComponent;
  const shouldAllowLinkingWorkflow =
    isNil(savedComponent) ||
    (!hasRelatedCardFilter(savedComponent) && !hasRelatedCardEntityFilter(savedComponent));
  const showRelatedCardRow =
    hasRelatedCardEntityFilter(component) &&
    !savedWorkflowTemplate?.loading &&
    !isNil(savedWorkflowTemplate?.workflowTemplate);

  const selectedWorkflowTemplateId = component.workflowTemplateId;
  const selectedWorkflow = useWorkflowTemplate(selectedWorkflowTemplateId);
  const selectedWorkflowTemplate = !isNil(selectedWorkflow.workflowTemplate)
    ? {
        workflowTemplateId: selectedWorkflow.workflowTemplate.id,
        imageUrl: selectedWorkflow.workflowTemplate.channelDefaultConfiguration?.imageUrl,
        name:
          workspaceDetails.get(selectedWorkflow.workflowTemplate.id)?.name ??
          selectedWorkflow.workflowTemplate.name,
        components: selectedWorkflow.workflowTemplate.components,
      }
    : null;

  const { onSelectFilteredComponent, onSelectLinkedComponent } = useLinkingOptions(component);
  const [hasQuantity, setHasQuantity] = useSetComponentField<
    boolean,
    MBAPI.WorkflowTemplate_components_RelatedCardComponent
  >(component, 'hasQuantity');

  return function SectionGenerator() {
    return (
      <>
        <>
          <View paddingHorizontal={3} paddingVertical={1}>
            <MBTextOverlineBold color={colors.foregroundInactive}>
              {i18n('builder.relatedCardOptions.selectWorkflowHeader')}
            </MBTextOverlineBold>
          </View>
          {shouldAllowLinkingWorkflow && (
            <RCSubMenu
              key="relatedCardRow"
              style={{ zIndex: 999 }}
              title={
                <RelatedCardLinkingRow
                  component={component}
                  selectedWorkflow={selectedWorkflowTemplate}
                  enabled={true}
                />
              }
            >
              <RelatedCardLinkingView component={component} workspaceDetails={workspaceDetails} />
            </RCSubMenu>
          )}
          {!shouldAllowLinkingWorkflow && (
            <>
              {selectedWorkflow.loading && <MBLoadingIndicator />}
              {!selectedWorkflow.loading && (
                <RelatedCardLinkingRow
                  component={component}
                  selectedWorkflow={selectedWorkflowTemplate}
                  enabled={false}
                />
              )}
            </>
          )}
        </>
        {showRelatedCardRow && (
          <>
            <View paddingHorizontal={3} paddingVertical={1}>
              <MBTextOverlineBold color={colors.foregroundInactive}>
                {i18n('builder.relatedCardOptions.filterOptionsRowHeader')}
              </MBTextOverlineBold>
            </View>
            <RCSubMenu
              key="relatedCardFilterRow"
              style={{ zIndex: 999 }}
              title={<RelatedCardFilterRow />}
            >
              <RelatedCardFilterView
                workflow={{
                  workflowTemplateId: savedWorkflowTemplate.workflowTemplate.id,
                  imageUrl:
                    savedWorkflowTemplate.workflowTemplate.channelDefaultConfiguration?.imageUrl,
                  name: savedWorkflowTemplate.workflowTemplate.name,
                  components: savedWorkflowTemplate.workflowTemplate.components,
                }}
                loading={savedWorkflowTemplate.loading}
                selectedWorkflow={selectedWorkflowTemplate}
                component={component}
                onSelectFilteredComponent={onSelectFilteredComponent}
                onSelectLinkedComponent={onSelectLinkedComponent}
              />
            </RCSubMenu>
            <RCSubMenu
              popupClassName={styles.sortWrapper}
              key="relatedCardSortRow"
              style={{ zIndex: 999 }}
              title={<RelatedCardSortRow />}
            >
              <RelatedCardSortView
                loading={savedWorkflowTemplate.loading}
                workflow={selectedWorkflowTemplate}
                component={component}
              />
            </RCSubMenu>
          </>
        )}
        {(hasRelatedCardFilter(component) || hasRelatedCardEntityFilter(component)) && (
          <>
            <View paddingHorizontal={3} paddingVertical={1}>
              <MBTextOverlineBold color={colors.foregroundInactive}>
                {i18n('builder.defaultRow.rowHeader')}
              </MBTextOverlineBold>
            </View>
            <RCSubMenu
              key="defaultValues"
              style={{ zIndex: 999 }}
              title={<ComponentDefaultItemRow component={component} />}
            >
              <DefaultSelection workflowTemplateId={workflowTemplateId} component={component} />
            </RCSubMenu>
          </>
        )}
        <View paddingHorizontal={3} paddingVertical={1}>
          <MBTextOverlineBold color={colors.foregroundInactive}>
            {i18n('builder.relatedCardOptions.quantity')}
          </MBTextOverlineBold>
          <View paddingTop={1} row centerVertically>
            <Checkbox checked={hasQuantity} onChange={setHasQuantity} />
            <MBTextBody1 align="left">{i18n('builder.relatedCardOptions.hasQuantity')}</MBTextBody1>
          </View>
        </View>
      </>
    );
  };
}
